// Admin v2 — Felhasználó kezelés (Figma ADM_02): statkártyák, keresés, szerep-szűrő, táblázat, ⋮ menü.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { AdminUser, fmtDate, fmtInt, normalizeUser, Role, ROLE_LABEL, ROLE_TONE, roleOf } from '../data';
import { ActionMenu, Avatar, Badge, Button, Empty, Input, PageHeader, Segmented, Spinner, StatCard } from '../ui';
import { AddUserModal, Dialog, UserDialogs, userMenuItems } from './userActions';

type Filter = 'all' | 'admin' | 'venueOperator' | 'coach';
const PAGE = 25;

export default function UsersPage() {
  const { user: me } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [venueNames, setVenueNames] = useState<Record<string, string>>({});
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [shown, setShown] = useState(PAGE);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const [u, v] = await Promise.all([getDocs(collection(db, 'users')), getDocs(collection(db, 'venues')).catch(() => null)]);
      setUsers(u.docs.map((d) => normalizeUser(d.id, d.data())));
      const names: Record<string, string> = {};
      v?.docs.forEach((d) => { names[d.id] = String(d.data().name ?? ''); });
      setVenueNames(names);
    } catch (e) {
      console.warn('[users] betöltés', e);
      setUsers([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => setShown(PAGE), [q, filter]);

  const counts = useMemo(() => {
    const l = users ?? [];
    return {
      all: l.length,
      admin: l.filter((u) => u.isAdmin).length,
      venueOperator: l.filter((u) => u.isVenueOperator).length,
      coach: l.filter((u) => u.isCoach).length,
    };
  }, [users]);

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (users ?? [])
      .filter((u) => filter === 'all' || (filter === 'admin' ? u.isAdmin : filter === 'venueOperator' ? u.isVenueOperator : u.isCoach))
      .filter((u) => !s || [u.displayName, u.email, u.username].some((f) => f.toLowerCase().includes(s)))
      .sort((a, b) => {
        if (a.uid === me?.uid) return -1;
        if (b.uid === me?.uid) return 1;
        return (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0);
      });
  }, [users, q, filter, me]);

  return (
    <div>
      <PageHeader
        title="Felhasználó kezelés"
        sub="Felhasználók kezelése és jogosultságok beállítása"
        right={<Button icon="UserPlus" onClick={() => setAdding(true)}>Felhasználó hozzáadása</Button>}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon="Users" tone="cyan" label="Összes felhasználó" value={fmtInt(counts.all)} />
        <StatCard icon="ShieldStar" tone="cyan" label="Adminok" value={fmtInt(counts.admin)} />
        <StatCard icon="Buildings" tone="gold" label="Üzemeltetők" value={fmtInt(counts.venueOperator)} />
        <StatCard icon="GraduationCap" tone="purple" label="Edzők" value={fmtInt(counts.coach)} />
      </div>

      <div className="flex items-center gap-3 mt-6 flex-wrap">
        <Input
          icon="MagnifyingGlass"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Keresés név vagy email alapján..."
          className="flex-1 min-w-[260px]"
        />
        <Segmented<Filter>
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: 'Mind' },
            { value: 'admin', label: 'Admin' },
            { value: 'venueOperator', label: 'Üzemeltető' },
            { value: 'coach', label: 'Edző' },
          ]}
        />
      </div>

      <div className="mt-6 bg-[#15191E] border border-[#31383D] rounded-2xl overflow-hidden">
        {users === null ? <Spinner /> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-left">
              <thead>
                <tr className="bg-[#1d232a] text-[12px] font-semibold uppercase text-[#8a8f99]">
                  <th className="pl-5 pr-4 h-[39px] font-semibold w-[24%]">Név</th>
                  <th className="px-4 font-semibold w-[23%]">Email</th>
                  <th className="px-4 font-semibold w-[13%]">Szerep</th>
                  <th className="px-4 font-semibold w-[17%]">Helyszín</th>
                  <th className="px-4 font-semibold w-[12%]">Létrehozva</th>
                  <th className="px-4 font-semibold w-[11%]">Műveletek</th>
                </tr>
              </thead>
              <tbody>
                {list.slice(0, shown).map((u) => {
                  const role: Role = roleOf(u);
                  const venues = u.venueIds.map((id) => venueNames[id]).filter(Boolean);
                  return (
                    <tr
                      key={u.uid}
                      onClick={() => nav(`/users/${u.uid}`)}
                      className="border-t border-[#31383D] h-[63px] cursor-pointer hover:bg-white/[0.02] transition"
                    >
                      <td className="pl-5 pr-4">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <Avatar src={u.avatarUrl} name={u.displayName} size={32} />
                          <div className="min-w-0">
                            <div className="text-[14px] font-semibold text-white truncate">{u.displayName}</div>
                            <div className="text-[12px] text-[#8a8f99] truncate">
                              {u.username || '—'}{u.uid === me?.uid ? ' (te)' : ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 text-[14px] text-[#B8B8C3] truncate max-w-[260px]">{u.email || '—'}</td>
                      <td className="px-4">
                        <div className="flex gap-1.5 flex-wrap">
                          <Badge tone={ROLE_TONE[role]}>{ROLE_LABEL[role]}</Badge>
                          {u.isAdmin && u.isVenueOperator && <Badge tone="gold">Üzemeltető</Badge>}
                        </div>
                      </td>
                      <td className="px-4 text-[14px] text-[#B8B8C3] truncate max-w-[200px]">{venues.length ? venues.join(', ') : '—'}</td>
                      <td className="px-4 text-[14px] text-[#B8B8C3]">{fmtDate(u.createdAt)}</td>
                      <td className="px-4" onClick={(e) => e.stopPropagation()}>
                        <ActionMenu items={userMenuItems(u, setDialog, [
                          { icon: 'UserCircle', label: 'Játékos profil', onClick: () => nav(`/users/${u.uid}`) },
                        ])} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {list.length === 0 && <Empty>Nincs a keresésnek megfelelő felhasználó.</Empty>}
            {list.length > shown && (
              <div className="border-t border-[#31383D] p-4 flex items-center justify-between text-[13px] text-[#8a8f99]">
                <span>{fmtInt(shown)} / {fmtInt(list.length)} felhasználó</span>
                <Button kind="ghost" onClick={() => setShown((s) => s + PAGE)}>Továbbiak betöltése</Button>
              </div>
            )}
          </div>
        )}
      </div>

      <UserDialogs dialog={dialog} close={() => setDialog(null)} reload={load} selfUid={me?.uid} />
      {adding && <AddUserModal onClose={() => setAdding(false)} onSaved={load} />}
    </div>
  );
}
